import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleList } from '@fortawesome/free-solid-svg-icons';
import {
  DOCUMENT_TYPE_FIELD_LABELS,
  DOCUMENT_TYPE_FIELDS,
  EMAIL_JOIN_LABELS,
  EMAIL_JOIN_TYPES,
} from './configConstants';
import TitleBuilder from './TitleBuilder';

const DocumentConfigSection = ({
  documentSection,
  isEditingConfig,
  saving,
  onOpenTemplateManager,
}) => (
  <div className="config-section">
    <div className="section-header">
      <h2>Document Configuration</h2>
    </div>

    <div className="section-content">
      <TitleBuilder
        titleComponents={documentSection.titleComponents}
        isEditingConfig={isEditingConfig}
        editingTitleComponent={documentSection.editingTitleComponent}
        setEditingTitleComponent={documentSection.setEditingTitleComponent}
        onAddToken={documentSection.handleAddToken}
        onAddLiteral={documentSection.handleAddLiteral}
        onMoveComponent={documentSection.handleMoveComponent}
        onDeleteComponent={documentSection.handleDeleteComponent}
        onTextCaseChange={documentSection.handleTextCaseChange}
        onSpaceReplacementChange={documentSection.handleSpaceReplacementChange}
        onDateStyleChange={documentSection.handleDateStyleChange}
        onLiteralEdit={documentSection.handleLiteralEdit}
      />

      <div className="config-subsection">
        <h3>Communication Templates</h3>
        <div className="retention-description" style={{ marginBottom: '15px' }}>
          Manage message templates used for patient communication. These templates can be used when sending messages to patients.
        </div>
        <button
          className="other-button edit-button"
          onClick={onOpenTemplateManager}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <FontAwesomeIcon icon={faRectangleList} />
          Manage Message Templates
        </button>
      </div>

      <div className="config-subsection">
        <h3>Document Retention Days</h3>
        <div className="retention-input-container">
          <input
            type="number"
            min="1"
            max="365"
            value={documentSection.retentionDays}
            onChange={(e) => isEditingConfig && documentSection.setRetentionDays(parseInt(e.target.value) || 1)}
            disabled={!isEditingConfig || saving}
            className={`retention-input ${!isEditingConfig ? 'disabled' : ''}`}
          />
          <span className="retention-label">days</span>
        </div>
        <div className="retention-description">
          Filed away documents will be automatically deleted after this many days. Minimum: 1 day, Maximum: 365 days.
        </div>
      </div>

      <div className="config-subsection">
        <h3>Document type</h3>
        <div className="config-document-type-field-description retention-description">
          Choose which field is used as the document type when filing away to the clinical system. New documents use this default; users can override it per document.
        </div>
        <div className="config-document-type-field-toggle-shell">
          <div
            className="config-document-type-field-toggle"
            role="group"
            aria-label="Default document type field"
          >
            {DOCUMENT_TYPE_FIELDS.map((field) => (
              <button
                key={field}
                type="button"
                className={`config-document-type-field-toggle-btn ${documentSection.defaultDocumentTypeField === field ? 'is-selected' : ''}`}
                onClick={() => isEditingConfig && !saving && documentSection.setDefaultDocumentTypeField(field)}
                disabled={!isEditingConfig || saving}
              >
                {DOCUMENT_TYPE_FIELD_LABELS[field]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="config-subsection">
        <h3>File away</h3>
        <label
          className={`config-checkbox-row ${!isEditingConfig || saving ? 'is-disabled' : ''}`}
        >
          <input
            type="checkbox"
            checked={documentSection.shouldIncludeReceivedAt}
            onChange={(e) =>
              isEditingConfig &&
              !saving &&
              documentSection.setShouldIncludeReceivedAt(e.target.checked)
            }
            disabled={!isEditingConfig || saving}
            className="config-setting-checkbox"
          />
          <span>Include received time when filing a document</span>
        </label>
        <div className="retention-description">
          When enabled, a comment is added under the filed-away line showing when the document was received.
        </div>
      </div>

      <div className="config-subsection">
        <h3>Email import</h3>
        <div className="config-email-import-description retention-description">
          Choose how inbound email is turned into a document for parsing. &ldquo;Email &amp; attachment&rdquo; combines the message body with attachments; &ldquo;Attachment only&rdquo; uses attachments alone.
        </div>
        <div className="config-email-join-toggle-shell">
          <div
            className="config-email-join-toggle"
            role="group"
            aria-label="Email import parsing mode"
          >
            {EMAIL_JOIN_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`config-email-join-toggle-btn ${documentSection.emailJoinType === type ? 'is-selected' : ''}`}
                onClick={() => isEditingConfig && !saving && documentSection.setEmailJoinType(type)}
                disabled={!isEditingConfig || saving}
              >
                {EMAIL_JOIN_LABELS[type]}
              </button>
            ))}
          </div>
        </div>
        <label
          className={`config-checkbox-row ${!isEditingConfig || saving ? 'is-disabled' : ''}`}
        >
          <input
            type="checkbox"
            checked={documentSection.shouldSendEmailSuccessAck}
            onChange={(e) =>
              isEditingConfig &&
              !saving &&
              documentSection.setShouldSendEmailSuccessAck(e.target.checked)
            }
            disabled={!isEditingConfig || saving}
            className="config-setting-checkbox"
          />
          <span>Send success acknowledgement email when inbound email is processed</span>
        </label>
        <div className="retention-description">
          When enabled, senders receive a reply confirming their email was processed successfully. Failure and rejection replies are always sent.
        </div>
      </div>
    </div>
  </div>
);

export default DocumentConfigSection;
